import { useState, useEffect } from "react";
import { useNavigate, useSearchParams, Link } from "react-router-dom";
import { resetPassword } from "../../services/authService";
import { LockClosedIcon, EyeIcon, EyeSlashIcon, ArrowRightIcon, CheckCircleIcon } from "@heroicons/react/24/outline";

export default function ResetPasswordPage() {
  const [searchParams] = useSearchParams();
  const token = searchParams.get("token");
  const navigate = useNavigate();

  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState(false);

  useEffect(() => {
    if (!token) {
      setError("Link reset tidak valid. Silakan request ulang dari halaman login.");
    }
  }, [token]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");

    if (newPassword.length < 8) {
      setError("Password minimal 8 karakter.");
      return;
    }

    if (newPassword !== confirmPassword) {
      setError("Password dan konfirmasi tidak cocok.");
      return;
    }

    setLoading(true);

    try {
      await resetPassword(token, newPassword);
      setSuccess(true);
      // Redirect ke login setelah 3 detik
      setTimeout(() => navigate("/login"), 3000);
    } catch (err) {
      console.error(err);
      setError(err.response?.data?.message || "Gagal mereset password. Coba lagi.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      {/* INJECT FONT & ANIMATION */}
      <style>
        {`@import url('https://fonts.googleapis.com/css2?family=Plus+Jakarta+Sans:wght@400;500;600;700;800&display=swap');
          .font-jakarta { font-family: 'Plus Jakarta Sans', sans-serif; }
          @keyframes fadeIn {
            from { opacity: 0; transform: translateY(10px); }
            to { opacity: 1; transform: translateY(0); }
          }
          .animate-fadeIn {
            animation: fadeIn 0.5s ease-out forwards;
          }
        `}
      </style>

      <div className="font-jakarta min-h-screen bg-[#0B1120] text-white flex items-center justify-center relative overflow-hidden p-4">
        
        {/* --- BACKGROUND AMBIANCE --- */}
        <div className="absolute top-0 left-0 w-full h-full overflow-hidden z-0">
          <div className="absolute top-[-10%] right-[20%] w-[500px] h-[500px] bg-indigo-600/20 rounded-full mix-blend-screen filter blur-[120px] animate-pulse duration-[8s]"></div>
          <div className="absolute bottom-[-10%] left-[20%] w-[500px] h-[500px] bg-blue-600/20 rounded-full mix-blend-screen filter blur-[120px] animate-pulse delay-1000 duration-[10s]"></div>
        </div>

        {/* --- CARD --- */}
        <div className="z-10 w-full max-w-md bg-card-bg border border-ledger-border backdrop-blur-xl rounded-3xl shadow-2xl p-8 md:p-10 relative animate-fadeIn opacity-0">
          
          {/* Logo */}
          <div className="flex justify-center mb-6">
            <img src="/tacia-icon.png" alt="Tacia Icon" className="h-10 w-10 object-contain drop-shadow-md" />
          </div>

          {success ? (
            /* SUCCESS STATE */
            <div className="text-center space-y-4">
              <div className="flex justify-center">
                <CheckCircleIcon className="h-16 w-16 text-emerald-500" />
              </div>
              <h3 className="text-2xl font-bold text-text-pri">Password Berhasil Direset!</h3>
              <p className="text-text-sec text-sm">
                Anda akan dialihkan ke halaman login dalam beberapa detik...
              </p>
              <Link 
                to="/login" 
                className="inline-block mt-4 px-6 py-3 bg-brand text-white rounded-xl font-bold text-sm hover:bg-blue-600 transition shadow-md"
              >
                Login Sekarang
              </Link>
            </div>
          ) : (
            /* FORM STATE */
            <>
              <div className="mb-6 text-center">
                <h3 className="text-2xl font-bold text-text-pri">Buat Password Baru</h3>
                <p className="text-text-sec text-sm mt-1">Masukkan password baru untuk akun Anda.</p>
              </div>

              <form onSubmit={handleSubmit} className="space-y-5">
                {error && (
                  <div className="p-3 bg-red-50 text-red-600 text-sm rounded-lg border border-red-200">
                    {error}
                  </div>
                )}

                {/* New Password */}
                <div className="space-y-1">
                  <label className="text-xs font-bold text-text-sec uppercase tracking-wide ml-1">Password Baru</label>
                  <div className="relative group">
                    <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                      <LockClosedIcon className="h-5 w-5 text-text-sec group-focus-within:text-brand transition" />
                    </div>
                    <input
                      type={showPassword ? "text" : "password"}
                      className="w-full pl-10 pr-10 py-3 bg-input-bg border border-ledger-border rounded-xl focus:ring-2 focus:ring-brand/20 focus:border-brand transition outline-none text-sm font-medium text-text-pri shadow-xs"
                      placeholder="Min. 8 karakter"
                      value={newPassword}
                      onChange={(e) => setNewPassword(e.target.value)}
                      required
                      disabled={!token}
                    />
                    <button
                      type="button"
                      onClick={() => setShowPassword(!showPassword)}
                      className="absolute inset-y-0 right-0 pr-3 flex items-center text-text-sec hover:text-brand transition focus:outline-none"
                    >
                      {showPassword ? <EyeSlashIcon className="h-5 w-5" /> : <EyeIcon className="h-5 w-5" />}
                    </button>
                  </div>
                </div>

                {/* Confirm Password */}
                <div className="space-y-1">
                  <label className="text-xs font-bold text-text-sec uppercase tracking-wide ml-1">Konfirmasi Password</label>
                  <div className="relative group">
                    <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                      <LockClosedIcon className="h-5 w-5 text-text-sec group-focus-within:text-brand transition" />
                    </div>
                    <input
                      type={showPassword ? "text" : "password"}
                      className="w-full pl-10 pr-4 py-3 bg-input-bg border border-ledger-border rounded-xl focus:ring-2 focus:ring-brand/20 focus:border-brand transition outline-none text-sm font-medium text-text-pri shadow-xs"
                      placeholder="Ketik ulang password baru"
                      value={confirmPassword}
                      onChange={(e) => setConfirmPassword(e.target.value)}
                      required
                      disabled={!token}
                    />
                  </div>
                </div>

                <button
                  type="submit"
                  disabled={loading || !token}
                  className="w-full py-3.5 bg-brand text-white rounded-xl font-bold text-sm hover:bg-blue-600 transition shadow-xl shadow-brand/20 flex items-center justify-center gap-2 group disabled:opacity-70"
                >
                  {loading ? "Menyimpan..." : "Reset Password"}
                  {!loading && <ArrowRightIcon className="w-4 h-4 group-hover:translate-x-1 transition" />}
                </button>
              </form>

              <div className="mt-6 text-center">
                <Link to="/login" className="text-sm text-slate-500 hover:text-indigo-600 transition">
                  ← Kembali ke Login
                </Link>
              </div>
            </>
          )}

          <div className="mt-6 text-center">
            <span className="text-[10px] text-slate-400 font-medium">Secured by Wibi Digital Technology</span>
          </div>
        </div>
      </div>
    </>
  );
}
